import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheck, 
  faSpinner, 
  faArrowLeft, 
  faArrowRight,
  faClipboardList,
  faUpload,
  faEye,
  faEnvelope,
  faUser,
  faBuilding,
  faPhone,
  faGlobe,
  faFileAlt,
  faCalendar,
  faLock,
  faPlusCircle,
  faMinusCircle,
  faSave
} from '@fortawesome/free-solid-svg-icons';

const WorkflowRenderer = ({ definition, agentId, token, onClose }) => {
  const [currentStepId, setCurrentStepId] = useState(definition.config?.start_step || definition.steps[0]?.id);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  // Icon mapping for dynamic icons
  const iconMapping = {
    'check': faCheck,
    'spinner': faSpinner,
    'arrow-left': faArrowLeft,
    'arrow-right': faArrowRight,
    'clipboard-list': faClipboardList,
    'upload': faUpload,
    'eye': faEye,
    'envelope': faEnvelope,
    'user': faUser,
    'building': faBuilding,
    'phone': faPhone,
    'globe': faGlobe,
    'file-alt': faFileAlt,
    'calendar': faCalendar,
    'lock': faLock,
    'plus-circle': faPlusCircle,
    'minus-circle': faMinusCircle,
    'save': faSave
  };

  const currentStep = definition.steps.find(step => step.id === currentStepId);
  const stepIndex = definition.steps.findIndex(step => step.id === currentStepId);
  const totalSteps = definition.steps.length;

  // Handle input changes
  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  // Validate current step
  const validateCurrentStep = () => {
    const newErrors = {};
    
    currentStep.sections?.forEach(section => {
      section.fields?.forEach(field => {
        if (field.required && (!formData[field.name] || formData[field.name].trim() === '')) {
          newErrors[field.name] = `${field.label} is required`;
        }
        
        // Email validation
        if (field.type === 'email' && formData[field.name]) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(formData[field.name])) {
            newErrors[field.name] = 'Please enter a valid email address';
          }
        }
      });
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle actions (next, back, submit)
  const handleAction = async (action, actionConfig) => {
    console.log('🔍 WorkflowRenderer handleAction:', { action, actionConfig, currentStepId });

    // Handle close action first
    if (action === 'cancel' || actionConfig?.type === 'close') {
      onClose();
      return;
    }

    // Handle direct navigation (back button or simple goto)
    if (actionConfig?.goto && !actionConfig.endpoint) {
      console.log('🔄 Direct navigation to:', actionConfig.goto);
      setCurrentStepId(actionConfig.goto);
      return;
    }

    // Handle actions with endpoints (next, submit)
    if (actionConfig?.endpoint) {
      console.log('🌐 API action with endpoint:', actionConfig.endpoint);
      
      if (!validateCurrentStep()) {
        console.log('❌ Validation failed');
        return;
      }
      
      setLoading(true);
      try {
        const endpoint = actionConfig.endpoint?.replace('{agent_id}', agentId);
        const requestData = {
          step: actionConfig.step || currentStepId,
          data: formData,
          workflow_id: definition.metadata.name
        };
        
        console.log('📤 Making API request:', { endpoint, requestData });
        
        const response = await fetch(endpoint, {
          method: actionConfig.method || 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        });
        
        if (response.ok) {
          console.log('✅ API request successful');
          
          // Mark current step as completed
          setCompletedSteps(prev => new Set([...prev, currentStepId]));
          
          // Show success message if provided
          if (actionConfig.success_message) {
            alert(actionConfig.success_message);
          }
          
          // Handle success action
          if (actionConfig.on_success?.goto) {
            console.log('🔄 Navigating to next step:', actionConfig.on_success.goto);
            setCurrentStepId(actionConfig.on_success.goto);
          } else if (actionConfig.on_success?.type === 'close') {
            console.log('🔚 Closing workflow');
            onClose();
          }
        } else {
          throw new Error(actionConfig.error_message || 'Step submission failed');
        }
      } catch (error) {
        console.error('❌ API request failed:', error);
        alert(error.message);
        // on_failure: "stay" is default behavior
      } finally {
        setLoading(false);
      }
      return;
    }
    
    console.log('❓ Unknown workflow action:', action, actionConfig);
  };

  // Render form field
  const renderField = (field) => {
    const commonProps = {
      id: field.name,
      name: field.name,
      className: `w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        errors[field.name] ? 'border-red-500' : ''
      }`,
      placeholder: field.placeholder,
      value: formData[field.name] || '',
      onChange: (e) => handleInputChange(field.name, e.target.value),
      disabled: loading
    };

    switch (field.type) {
      case 'text':
      case 'email':
        return <input type={field.type} {...commonProps} />;
        
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={field.rows || 3}
          />
        );
        
      case 'select':
        return (
          <select {...commonProps}>
            <option value="">Select {field.label}</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
        
      case 'checkbox':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              id={field.name}
              name={field.name}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              checked={formData[field.name] || false}
              onChange={(e) => handleInputChange(field.name, e.target.checked)}
              disabled={loading}
            />
            <label htmlFor={field.name} className="ml-2 block text-sm text-gray-900">
              {field.label}
            </label>
          </div>
        );
        
      case 'file':
        return (
          <div>
            <input
              type="file"
              id={field.name}
              name={field.name}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              accept={field.accept}
              onChange={(e) => {
                const file = e.target.files[0];
                handleInputChange(field.name, file ? file.name : '');
              }}
              disabled={loading}
            />
            {field.accept && (
              <p className="text-sm text-gray-500 mt-1">
                Accepted formats: {field.accept}
              </p>
            )}
          </div>
        );
        
      default:
        return <input type="text" {...commonProps} />;
    }
  };

  // Render progress indicator
  const renderProgressIndicator = () => {
    if (!definition.config?.show_progress) return null;
    
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          {definition.steps.map((step, index) => {
            const isCompleted = completedSteps.has(step.id);
            const isCurrent = step.id === currentStepId;
            const isPast = definition.steps.findIndex(s => s.id === currentStepId) > index;
            
            return (
              <div key={step.id} className="flex items-center">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${isCurrent ? 'bg-blue-600 text-white' : 
                    isCompleted || isPast ? 'bg-green-600 text-white' : 
                    'bg-gray-200 text-gray-600'}
                `}>
                  {isCompleted || isPast ? (
                    <FontAwesomeIcon icon={faCheck} className="w-4 h-4" />
                  ) : (
                    step.order || index + 1
                  )}
                </div>
                
                {index < definition.steps.length - 1 && (
                  <div className={`
                    w-12 h-1 mx-2
                    ${isPast || isCompleted ? 'bg-green-600' : 'bg-gray-200'}
                  `} />
                )}
              </div>
            );
          })}
        </div>
        
        <div className="text-center">
          <span className="text-sm text-gray-600">
            Step {stepIndex + 1} of {totalSteps}: {currentStep?.title}
          </span>
        </div>
      </div>
    );
  };

  // Render action buttons
  const renderActions = () => {
    if (!currentStep?.actions) return null;
    
    return (
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <div>
          {currentStep.actions.back && (
            <button
              onClick={() => handleAction('back', currentStep.actions.back)}
              disabled={loading}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Back
            </button>
          )}
          
          {currentStep.actions.cancel && (
            <button
              onClick={() => handleAction('cancel', currentStep.actions.cancel)}
              disabled={loading}
              className="ml-2 px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
          )}
        </div>
        
        <div>
          {currentStep.actions.next && (
            <button
              onClick={() => handleAction('next', currentStep.actions.next)}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
              ) : (
                <FontAwesomeIcon icon={faArrowRight} className="mr-2" />
              )}
              Next
            </button>
          )}
          
          {currentStep.actions.submit && (
            <button
              onClick={() => handleAction('submit', currentStep.actions.submit)}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
              ) : currentStep.actions.submit.icon ? (
                <FontAwesomeIcon 
                  icon={iconMapping[currentStep.actions.submit.icon] || faSave} 
                  className="mr-2" 
                />
              ) : null}
              Submit
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render review step
  const renderReviewStep = () => {
    if (currentStep?.type !== 'review') return null;
    
    return (
      <div className="space-y-4">
        {currentStep.content?.message && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-blue-800">{currentStep.content.message}</p>
          </div>
        )}
        
        {currentStep.content?.show_summary && (
          <div className="bg-gray-50 rounded-md p-4">
            <h4 className="font-medium text-gray-900 mb-3">Summary:</h4>
            <div className="grid grid-cols-1 gap-2 text-sm">
              {Object.entries(formData).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span className="text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                  <span className="text-gray-900">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!currentStep) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error: Step '{currentStepId}' not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
      {/* Progress Indicator */}
      {renderProgressIndicator()}
      
      {/* Step Title */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {currentStep.title}
        </h2>
        {currentStep.description && (
          <p className="text-gray-600 mt-1">{currentStep.description}</p>
        )}
      </div>
      
      {/* Step Content */}
      <div className="space-y-6">
        {/* Review Step */}
        {renderReviewStep()}
        
        {/* Form Sections */}
        {currentStep.sections?.map((section, sectionIndex) => (
          <div key={`section-${sectionIndex}`} className={section.css_class || ''}>
            {section.title && (
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {section.title}
              </h3>
            )}
            
            {section.fields?.map((field) => (
              <div key={field.name} className="mb-4">
                <label
                  htmlFor={field.name}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                
                {renderField(field)}
                
                {errors[field.name] && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors[field.name]}
                  </p>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
      
      {/* Action Buttons */}
      {renderActions()}
    </div>
  );
};

export default WorkflowRenderer;